import { memo } from 'react';
import { useSelector } from 'react-redux';
import {
  getRxLineClasses,
  itemSettingStyle,
  medicineFormat,
  printQTY,
} from '../../../utils/helpers';

const SelectedMedicineSchedule = ({
  item,
  data,
  typeName,
  element,
  rxWhite,
  prescriptionItems,
}) => {
  const medicineTypes = useSelector(
    (state) => state.medicineTypeReducer.medTypes,
  );

  const rxWidth = prescriptionItems?.items?.rx?.rxWidth;
  const toW = (entry) => {
    if (!entry) return null;
    if (typeof entry === 'object') return entry.value ? `${entry.value}${entry.unit ?? '%'}` : null;
    return `${entry}%`;
  };
  const scheduleW = toW(rxWidth?.schedule);
  const remarkW   = toW(rxWidth?.remark);
  const durationW = toW(rxWidth?.duration);

  const hasInstruction = data?.instructions?.length > 0;
  const hasDuration =
    data.durationUnit === 'চলবে' || (data.duration && data.duration !== '');
  const durationText = data.durationUnit === 'চলবে'
    ? data.durationUnit
    : data.duration
      ? data.duration + ' ' + data.durationUnit
      : '';
  const lineActive = element?.lineDraw && element?.lineType !== 'none';
  const lineNoneMode = element?.lineDraw && element?.lineType === 'none';

  const lineBgClass =
    element?.lineType === 'dash' ? 'background-dash'
    : element?.lineType === 'small-dash' ? 'background-small-dash'
    : element?.lineType === 'dot' ? 'background-dot'
    : element?.lineType === 'hiphen' ? 'background-hiphen'
    : element?.lineType === 'line' ? 'background-solid-line'
    : '';

  const wrapperClass = `rx-left-space ${item.parentId ? 'child-medicine-schedule' : ''}`;
  const wrapperBaseStyle = itemSettingStyle(prescriptionItems?.items?.rx?.subItemStyle || {});

  const scheduleContent = (
    <>
      {(medicineFormat(medicineTypes, item?.type, 'Tablet') ||
        medicineFormat(medicineTypes, item?.type, 'Inj. Tablet')) &&
        Object.values(data.quantities || {})?.some((val) => val !== 0) &&
        Object.values(data.quantities || {})?.map((val, index) => (
          <span key={index} className={rxWhite}>
            {printQTY(val)}
            {++index === Object.values(data.quantities).length
              ? ' ' + data.quantityUnit
              : ' + '}
          </span>
        ))}

      {medicineFormat(medicineTypes, item?.type, 'Inj. Tablet') &&
        Object.values(data.quantities)?.some((val) => val !== 0) &&
        data?.route !== '' && (
          <span className="rx-white"> (Route: {data?.route})</span>
        )}

      {(medicineFormat(medicineTypes, item?.type, 'Injection') ||
        medicineFormat(medicineTypes, item?.type, 'Syrup')) &&
        (data?.quantity || data?.schedule) && (
          <span className={rxWhite}>
            {data.quantity ? (
              <>
                {printQTY(data.quantity)} {data.quantityUnit}
              </>
            ) : (
              ''
            )}{' '}
            {data.schedule ? (
              <>
                {printQTY(data.schedule)} {data.scheduleUnit}
              </>
            ) : (
              ''
            )}
          </span>
        )}

      {medicineFormat(medicineTypes, item?.type, 'Injection') &&
        (data?.quantity || data?.schedule) &&
        data?.route !== '' && (
          <span className="rx-white"> (Route: {data?.route})</span>
        )}
    </>
  );

  // lineDraw checked + none selected: inline text with — separators, no fixed columns
  if (lineNoneMode) {
    return (
      <div className={wrapperClass} style={wrapperBaseStyle}>
        {scheduleContent}
        <span className={`pr-2 ${rxWhite}`}></span>
        {(hasInstruction || hasDuration) && (
          <span className={rxWhite}> — </span>
        )}
        {hasInstruction && (
          <span className={rxWhite}>{data?.instructions?.join(',  ')}</span>
        )}
        {hasDuration && (
          <>
            {hasInstruction && <span className={rxWhite}> — </span>}
            <span className={rxWhite}>{durationText}</span>
          </>
        )}
      </div>
    );
  }

  // Column layout: line backgrounds when lineActive, plain columns when lineDraw unchecked
  return (
    <div
      className={wrapperClass}
      style={{ ...wrapperBaseStyle, ...(lineActive ? { display: 'flex', alignItems: 'stretch' } : {}) }}
    >
      <span
        className={lineActive && (hasInstruction || hasDuration) ? getRxLineClasses(element, 'rx-schedule') : 'medicine-schedule'}
        style={scheduleW ? { width: scheduleW } : {}}
      >
        {scheduleContent}
        <span className={`pr-2 ${rxWhite}`}></span>
      </span>

      <span
        className="medicine-instruction"
        style={remarkW ? { width: remarkW } : {}}
      >
        {lineActive && (hasInstruction || hasDuration) ? (
          <span className={lineBgClass} style={{ flex: 1, alignSelf: 'stretch' }}></span>
        ) : (
          <span style={{ flex: 1 }}></span>
        )}
        <span
          className={rxWhite}
          style={{ padding: '0 4px', textAlign: 'center', wordBreak: 'break-word' }}
        >
          {data?.instructions?.join(',  ')}
        </span>
        {lineActive && hasDuration ? (
          <span className={lineBgClass} style={{ flex: 1, alignSelf: 'stretch' }}></span>
        ) : (
          <span className={rxWhite} style={{ flex: 1 }}></span>
        )}
      </span>

      <span
        className="medicine-duration"
        style={{
          ...(durationW ? { width: durationW } : {}),
          display: 'inline-flex',
          alignItems: 'center',
        }}
      >
        {lineActive && hasDuration && (
          <span className={lineBgClass} style={{ flex: 1, alignSelf: 'stretch' }}></span>
        )}
        <span className={rxWhite} style={{ paddingLeft: '4px', paddingRight: '3px', flexShrink: 0 }}>
          {durationText}
        </span>
      </span>

      {element?.lineDraw && element.lineType === 'underscore' && (
        <div className="bottom-border"></div>
      )}
    </div>
  );
};

export default memo(SelectedMedicineSchedule);
